import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { CreditCard, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Settings → Razorpay configuration.
 * Reads/writes app_settings._id="razorpay_config" via /api/smb/razorpay/settings.
 * Secrets are masked on read; only re-sent on write when a new non-empty
 * value is provided (so the admin can flip Test↔Live without re-typing).
 */
export const RazorpaySettingsCard = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    mode: 'test', key_id: '', key_secret: '', webhook_secret: '',
    checkout_name: '', checkout_description_activation: '', checkout_description_full: '',
    settlement_bank_account_id: '',
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const [r, banksRes] = await Promise.all([
        api.get('/smb/razorpay/settings'),
        api.get('/bank-accounts').catch(() => ({ data: { data: [] } })),
      ]);
      if (r.data?.success) {
        setData(r.data.data);
        setForm(f => ({
          ...f,
          mode: r.data.data.mode || 'test',
          key_id: r.data.data.key_id || '',
          checkout_name: r.data.data.checkout_name || '',
          checkout_description_activation: r.data.data.checkout_description_activation || '',
          checkout_description_full: r.data.data.checkout_description_full || '',
          settlement_bank_account_id: r.data.data.settlement_bank_account_id || '',
        }));
      }
      setBankAccounts(banksRes.data?.data || []);
    } catch {/* perm */}
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.put('/smb/razorpay/settings', form);
      toast.success('Razorpay settings saved');
      setForm(f => ({ ...f, key_secret: '', webhook_secret: '' }));
      load();
    } catch (er) { toast.error(er.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const copyWebhook = () => {
    if (!data?.webhook_url) return;
    navigator.clipboard.writeText(data.webhook_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  /**
   * One-shot "Sync historical Razorpay fees" — fetches fee+tax from
   * Razorpay for every paid link that doesn't yet have a paired
   * expense row, then books the missing expenses. Idempotent: safe to
   * click multiple times. Requires `settlement_bank_account_id` to be
   * configured first (server returns 400 if not).
   */
  const backfillFees = async () => {
    if (backfilling) return;
    if (!form.settlement_bank_account_id) {
      toast.error('Configure the settlement bank account first.');
      return;
    }
    setBackfilling(true);
    const tid = toast.loading('Syncing historical Razorpay fees…');
    try {
      // Run both backfills in parallel — the AMC/Hosting flow (via
      // payment_links) and the SMB conversion flow (via smb_payments)
      // both need their PG-fee expenses booked for accurate margin
      // reporting.
      const [rHost, rSmb] = await Promise.all([
        api.post('/payment-links/backfill-razorpay-fees'),
        api.post('/smb/backfill-razorpay-fees'),
      ]);
      const dH = rHost.data?.data || {};
      const dS = rSmb.data?.data || {};
      const totScanned = (dH.scanned || 0) + (dS.scanned || 0);
      const totCaptured = (dH.captured || 0) + (dS.captured || 0);
      const totBooked = (dH.booked || 0) + (dS.booked || 0);
      const totErrors = (dH.errors || 0) + (dS.errors || 0);
      toast.success(
        `Scanned ${totScanned} (AMC/Hosting: ${dH.scanned || 0}, SMB: ${dS.scanned || 0}), captured ${totCaptured}, booked ${totBooked}${totErrors ? `, ${totErrors} errors` : ''}.`,
        { id: tid },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed', { id: tid });
    } finally {
      setBackfilling(false);
    }
  };

  if (!data) return null;
  return (
    <Card data-testid="razorpay-settings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-blue-500" />Razorpay (SMB Activation Payments)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="flex items-center justify-between gap-2 p-3 rounded-md bg-muted/30">
            <div className="text-xs">
              <p className="font-mono break-all">{data.webhook_url}</p>
              <p className="text-muted-foreground mt-1">
                Add this URL to <strong>Razorpay Dashboard → Webhooks</strong> (subscribe to <code>payment.captured</code> and <code>payment_link.paid</code>), then paste the secret below.
              </p>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={copyWebhook} data-testid="copy-webhook-url">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Mode</Label>
              <div className="flex gap-2">
                {['test','live'].map(m => (
                  <button key={m} type="button"
                    className={`flex-1 px-3 py-1.5 rounded border text-xs font-medium ${form.mode===m?'bg-primary text-primary-foreground border-primary':'border-border'}`}
                    onClick={() => setForm({...form, mode: m})}
                    data-testid={`rzp-mode-${m}`}
                  >{m.toUpperCase()}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Badge className={data.configured ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'}>
                {data.configured ? 'Configured' : 'Not configured'}
              </Badge>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Key ID</Label>
            <Input placeholder="rzp_test_... or rzp_live_..." value={form.key_id} onChange={e => setForm({...form, key_id: e.target.value})} data-testid="rzp-key-id" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Key Secret {data.key_secret_masked && <span className="text-muted-foreground font-mono">(current: {data.key_secret_masked})</span>}</Label>
            <Input type="password" placeholder="Leave blank to keep current" value={form.key_secret} onChange={e => setForm({...form, key_secret: e.target.value})} data-testid="rzp-key-secret" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Webhook Secret {data.webhook_secret_masked && <span className="text-muted-foreground font-mono">(current: {data.webhook_secret_masked})</span>}</Label>
            <Input type="password" placeholder="Leave blank to keep current" value={form.webhook_secret} onChange={e => setForm({...form, webhook_secret: e.target.value})} data-testid="rzp-webhook-secret" />
          </div>

          {/* Customer-facing checkout copy. Both fields appear on the
              Razorpay checkout modal AND get propagated into the UPI
              memo (so Gpay shows "MyzowSolutionsLLPWebsiteActivationFee"
              rather than the SMB's business name). */}
          <div className="pt-2 mt-2 border-t border-border/40 space-y-3">
            <p className="text-xs text-muted-foreground font-medium">Checkout display (shown to customers)</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Payee name <span className="text-muted-foreground">(shown at top of Razorpay modal)</span></Label>
              <Input placeholder="Myzow Solutions LLP" value={form.checkout_name} onChange={e => setForm({...form, checkout_name: e.target.value})} data-testid="rzp-checkout-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Description — Activation (₹999)</Label>
                <Input placeholder="Website Activation Fee" value={form.checkout_description_activation} onChange={e => setForm({...form, checkout_description_activation: e.target.value})} data-testid="rzp-desc-activation" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description — Full Payment</Label>
                <Input placeholder="Website Build — Full Payment" value={form.checkout_description_full} onChange={e => setForm({...form, checkout_description_full: e.target.value})} data-testid="rzp-desc-full" />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground italic">
              Tip: keep these short — UPI apps (Gpay, PhonePe) concatenate the payee name + description into the transaction note.
            </p>
          </div>

          <Button type="submit" disabled={saving} data-testid="rzp-save">{saving ? 'Saving…' : 'Save Razorpay settings'}</Button>
        </form>

        {/* Settlement bank account + backfill — sits OUTSIDE the main
            form so its own button doesn't collide with the form-submit
            on Enter. Once a bank is picked, "Save settlement account"
            is what writes it; the backfill button then becomes usable. */}
        <div className="pt-4 mt-4 border-t border-border/40 space-y-3">
          <p className="text-xs font-medium">Settlement bank account</p>
          <p className="text-[11px] text-muted-foreground">
            Razorpay reimburses captured payments to this account. The auto
            tax-invoice credit row + paired Razorpay-fee expense row are both
            booked against it so book balances stay in lockstep with the
            actual deposit.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={form.settlement_bank_account_id}
              onChange={(e) => setForm({...form, settlement_bank_account_id: e.target.value})}
              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
              data-testid="rzp-settlement-bank"
            >
              <option value="">— Pick a bank account —</option>
              {bankAccounts.filter(b => !b.deleted_at).map(b => (
                <option key={b.id} value={b.id}>
                  {b.name} · {b.bank} · {b.account_number?.slice(-4)}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              onClick={save}
              disabled={saving}
              data-testid="rzp-save-settlement-bank"
            >
              Save settlement account
            </Button>
          </div>
          {!form.settlement_bank_account_id && (
            <p className="text-[11px] text-amber-500">
              ⚠ Required — auto invoice generation will fail until this is set.
            </p>
          )}

          <div className="pt-2 flex items-center justify-between gap-3">
            <div className="text-[11px] text-muted-foreground">
              Already-paid Razorpay payments (AMC/Hosting <strong>and</strong> SMB
              conversions) that pre-date this feature have no Razorpay-fee expense
              booked. Click to fetch fees from Razorpay + book the missing
              expense rows.
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={backfillFees}
              disabled={backfilling || !form.settlement_bank_account_id}
              data-testid="rzp-backfill-fees"
            >
              {backfilling ? 'Syncing…' : 'Sync historical Razorpay fees'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
